import {
  PRODUCT_PRODUCTION_TYPES,
  PRODUCT_TURNAROUNDS,
  type ProductionType,
  SCOPES,
  type Scope,
  type TurnaroundDays,
} from '@noconflict/contracts';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../../../shared/ui/card';
import { Field } from '../../../shared/ui/field';
import { Input as TextInput } from '../../../shared/ui/input';
import { Label } from '../../../shared/ui/label';
import { RadioCards } from '../../../shared/ui/radio-cards';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../shared/ui/select';
import { Textarea } from '../../../shared/ui/textarea';
import type { TitleDraft } from './model';

/** What a turnaround card says: when to order by, and when the report would arrive. */
export type TurnaroundEstimate = { cutoffText: string; deliveredText: string };

/** Slowest (cheapest) first, as the price list reads. */
const TURNAROUNDS = [...PRODUCT_TURNAROUNDS.title].sort((a, b) => b - a);
const PRODUCTION_TYPES = PRODUCT_PRODUCTION_TYPES.title ?? [];

/**
 * The title clearance order form. Every explanation is on the page: scope and turnaround are radio cards that say
 * what each choice means, and each turnaround shows when to order by and when the report would arrive, in the
 * viewer's own time. Nothing is submitted here: each field saves itself (`useTitleDraft`), and the price summary
 * beside the form holds the submit button.
 */
export function TitleOrderForm({
  draft,
  estimates,
  referenceRequired,
}: {
  draft: TitleDraft;
  /** Delivery estimates by turnaround; empty while the holidays load. */
  estimates: Map<number, TurnaroundEstimate>;
  referenceRequired: boolean;
}) {
  const { t } = useTranslation('order');
  const typeId = useId();
  const { values, set, commit, save } = draft;

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle asChild className="font-sans">
            <h2>{t('form.details')}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-5">
          <Field label={t('form.title')} description={t('form.titleHelp')}>
            <TextInput
              autoComplete="off"
              maxLength={200}
              required
              value={values.title}
              onChange={(e) => set({ title: e.target.value })}
              onBlur={save}
            />
          </Field>
          <div className="grid gap-2">
            <Label htmlFor={typeId}>{t('form.productionType')}</Label>
            <Select
              value={values.productionType ?? ''}
              onValueChange={(v) => commit({ productionType: v as ProductionType })}
            >
              <SelectTrigger id={typeId} aria-required="true">
                <SelectValue placeholder={t('form.productionTypePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {PRODUCTION_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {t(`form.productionTypes.${type}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Field label={t('form.company')} description={t('form.companyHelp')}>
            <TextInput
              autoComplete="organization"
              maxLength={200}
              value={values.company}
              onChange={(e) => set({ company: e.target.value })}
              onBlur={save}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle asChild className="font-sans">
            <h2>{t('form.choices')}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-6">
          <Field label={t('form.scope')} description={t('form.scopeHelp')}>
            <RadioCards
              required
              columns={2}
              value={values.scope}
              onValueChange={(v) => commit({ scope: v as Scope })}
              options={SCOPES.map((scope) => ({
                value: scope,
                title: t(`scope.${scope}`),
                description: t(`form.scopeOptions.${scope}`),
              }))}
            />
          </Field>
          <Field label={t('form.turnaround')} description={t('form.turnaroundHelp')}>
            <RadioCards
              required
              value={String(values.turnaroundDays)}
              onValueChange={(v) => commit({ turnaroundDays: Number(v) as TurnaroundDays })}
              options={TURNAROUNDS.map((days) => {
                const estimate = estimates.get(days);
                return {
                  value: String(days),
                  title: t('turnaround', { count: days }),
                  description: estimate?.cutoffText ?? t('estimate.loading'),
                  ...(estimate ? { trailing: estimate.deliveredText } : {}),
                };
              })}
            />
          </Field>
          {referenceRequired ? (
            <Field label={t('form.reference')} description={t('form.referenceHelp')}>
              <TextInput
                autoComplete="off"
                maxLength={80}
                required
                value={values.reference}
                onChange={(e) => set({ reference: e.target.value })}
                onBlur={save}
              />
            </Field>
          ) : null}
          <Field label={t('form.notes')} description={t('form.notesHelp')}>
            <Textarea
              maxLength={4000}
              value={values.notes}
              onChange={(e) => set({ notes: e.target.value })}
              onBlur={save}
            />
          </Field>
        </CardContent>
      </Card>
    </div>
  );
}
